'use client';

import { Card, CardFooter, CardHeader, CardAction } from '@packages/shared/src/ui/Card/Card';
import { FormattedMessage } from 'react-intl';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { CustomerStatusDocument } from '@packages/gql/generated/shopping/CustomerStatusDocument';
import type { CustomerAmount } from '@packages/gql/generated/shopping/graphql';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { Link } from '@packages/modules/src/Link/Link';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { CustomerCard } from './CustomerCard.client';
import { OfflineNotification } from '../OfflineNotification/OfflineNotification';
import { Maturities } from '../Maturities/Maturities.client';
import { ReturnsCard } from './ReturnsCard.client';
import { OrdersCard } from './OrdersCard.client';
import { JoeOverviewCard } from '../Joe/JoeOverviewCard.client';
import { resolveJoePlacement } from '../Joe/resolveJoePlacement';
import { useJoeConnection } from '../Joe/useJoeConnection';
import { JoeConnectionProvider } from '../Joe/JoeConnectionProvider.client';

/* GraphQL */ `
  query CustomerStatus($locale: String!) {
    customerStatus(locale: $locale) {
      ... on CustomerServerFailure {
        failureType
        failureMessage
      }
      ... on CustomerStatusError {
        statusErrorType
        statusErrorMessage
      }
      ... on CustomerStatus {
        maturities {
          currentMaturities {
            currencyCode
            value
          }
          grandTotal {
            currencyCode
            value
          }
          currentAccountBalance {
            currencyCode
            value
          }
        }
        financialDetails {
          minimumRate
          rdiAmount
          totalMinimumRate
          minimumRateDueDate
          isRdiAmountExcluded
        }
        returns {
          orderNo
          items {
            name
            variation
            url
            imageUrl
          }
          additionalItemCount
          date
          status
        }
        shipments {
          orderNo
          additionalItemCount
          date
          status
          items {
            name
            variation
            url
            imageUrl
          }
        }
        advantages {
          list
          links {
            description
            href
            iconType
          }
        }
        premiumStatus {
          kind
          startDate
          endDate
          renewalDate
          cancellable
        }
      }
    }
  }
`;

export const CustomerOverview = () => {
  const { locale } = useI18n();
  const {
    order: {
      paths: { myAccountBookings, myAccountPaymentsDates },
      supportedPaths: { myAccountPaymentsDates: paymentDatesAllowed },
      joe: { enabled: isJoeEnabled },
    },
  } = useConfig();
  const { state: joeState, ...joeActions } = useJoeConnection();
  const joePlacement = resolveJoePlacement(isJoeEnabled, joeState);
  const [{ data, error, fetching }] = useAuthQuery({
    query: CustomerStatusDocument,
    variables: { locale },
  });

  const renderLoadingSkeleton = () => (
    <div>
      <Skeleton className="my-1.5 h-10 w-64 px-2 md:pt-0" />
      <div className="bg-color-grey-light grid grid-cols-1 gap-2 p-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Skeleton className="h-6 w-6 rounded-full" />
              <div className="mx-2 flex flex-col gap-1">
                {['w-[10rem]', 'w-[20rem]', 'w-[15rem]'].map((width) => (
                  <Skeleton key={width} className={`h-2 ${width}`} />
                ))}
              </div>
            </div>
          </CardHeader>
          <div className="flex flex-col gap-2 p-4">
            {['w-[90%]', 'w-[80%]', 'w-[70%]', 'w-[60%]', 'w-[50%]'].map((width) => (
              <Skeleton key={width} className={`h-4 ${width}`} />
            ))}
          </div>
        </Card>
        {['skeleton-card-1', 'skeleton-card-2', 'skeleton-card-3'].map((key, index) => (
          <Card key={key}>
            <CardHeader className="mb-3 h-6.5">
              {
                ['w-[80%]', 'w-[90%]', 'w-[70%]'].map((width) => (
                  <Skeleton key={width} className={`h-6.5 ${width}`} />
                ))[index % 3]
              }
            </CardHeader>
            <div className="flex flex-col gap-2 p-4">
              {['line-1', 'line-2', 'line-3', 'line-4', 'line-5'].map((lineKey) => (
                <Skeleton key={lineKey} className="h-4" />
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  // Unified error handling: show OfflineNotification for GraphQL errors, null data, or DiVA errors
  if (
    data?.customerStatus.__typename === 'CustomerServerFailure' ||
    data?.customerStatus.__typename === 'CustomerStatusError' ||
    error
  ) {
    return <OfflineNotification />;
  }
  if ((!data?.customerStatus && fetching) || data?.customerStatus.__typename !== 'CustomerStatus') {
    // Show skeleton loading state while data is undefined or fetching
    return renderLoadingSkeleton();
  }

  const {
    maturities,
    financialDetails,
    advantages,
    shipments = [],
    returns = [],
  } = data.customerStatus;

  const fallbackBalance: CustomerAmount = {
    value: 0,
    currencyCode: 'EUR',
  };

  const balance = maturities?.currentAccountBalance ?? fallbackBalance;

  // Only a standalone placement changes the grid composition; `embedded` and `none` keep the
  // existing card order untouched.
  const standaloneJoeState =
    joePlacement === 'standalone' && joeState?.status !== 'connected' ? joeState : undefined;

  const customerCard = (
    <CustomerCard advantages={advantages} hasConnectedJoe={joePlacement === 'embedded'} />
  );

  const maturitiesCard = (
    <Maturities
      balance={balance}
      financialDetails={financialDetails}
      cardHeadline={
        <FormattedMessage
          id="CustomerOverview.Maturities.CardHeadline"
          defaultMessage="Rechnungen / Zahlungen"
        />
      }
      cardFooter={
        <>
          {paymentDatesAllowed && (
            <CardFooter className="flex items-center gap-2 px-2 py-1">
              <CardAction icon="icon-emp-payment">
                <Link href={getLocalizedPath(myAccountPaymentsDates, locale)}>
                  <FormattedMessage
                    id="CustomerOverview.Maturities.PaymentDatesLink"
                    defaultMessage="Übersicht nächste Zahltermine"
                  />
                </Link>
              </CardAction>
            </CardFooter>
          )}
          <CardFooter className="flex items-center gap-2 px-2 py-1">
            <CardAction icon="icon-emp-billing">
              <Link href={getLocalizedPath(myAccountBookings, locale)}>
                <FormattedMessage
                  id="CustomerOverview.Maturities.BookingsLink"
                  defaultMessage="Alle Buchungen"
                />
              </Link>
            </CardAction>
          </CardFooter>
        </>
      }
    />
  );

  const ordersCard = <OrdersCard shipments={shipments} />;
  const returnsCard = <ReturnsCard returns={returns} />;

  return (
    <JoeConnectionProvider value={joeActions}>
      <div className="bg-color-grey-light grid grid-cols-1 items-start gap-2 p-3 lg:grid-cols-2">
        {standaloneJoeState ? (
          <>
            <div className="space-y-2">
              {customerCard}
              <JoeOverviewCard state={standaloneJoeState} />
            </div>
            <div className="space-y-2">
              {maturitiesCard}
              {ordersCard}
              {returnsCard}
            </div>
          </>
        ) : (
          <>
            {customerCard}
            {maturitiesCard}
            {ordersCard}
            {returnsCard}
          </>
        )}
      </div>
    </JoeConnectionProvider>
  );
};
